import type { ExpertGoalBlock, ExpertGoalCheck, ExpertGoalStatus } from "@dante/contracts";
import { dantePresentationForGoal, hasDanteVerificationEvidence } from "@dante/core";
import {
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DanteCharacter,
  Input,
} from "@dante/ui-web";
import { Trans, useLingui } from "@lingui/react/macro";
import { ChevronDown } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { errorText } from "../lib/user-error";

/** How many Done-when checks the transcript has proven, for the card's N/M count. */
export function expertGoalCheckProgress(checks: readonly ExpertGoalCheck[]): {
  verified: number;
  total: number;
} {
  return {
    verified: checks.filter(hasDanteVerificationEvidence).length,
    total: checks.length,
  };
}

/** The transcript proof for a check, or null while the wrangler has not verified it. */
export function expertGoalEvidenceLine(check: ExpertGoalCheck): string | null {
  const evidence = check.evidence;
  if (!hasDanteVerificationEvidence(check) || !evidence) return null;
  const exit = evidence.exitCode == null ? "" : ` · exit ${evidence.exitCode}`;
  return `${evidence.tool}${exit} · ${evidence.excerpt.trim()}`;
}

/** What the card asks the person when the goal stopped short, or null when it did not. */
export function expertGoalBlockerQuestion(block: ExpertGoalBlock): string | null {
  const blocker = block.blocker;
  if (!blocker || blocker.resolvedAt) return null;
  return blocker.question ?? blocker.reason;
}

/** The compact label follows the reported goal status. */
function goalStatusLabel(status: ExpertGoalStatus): ReactNode {
  switch (status) {
    case "pending":
      return <Trans>Queued</Trans>;
    case "active":
      return <Trans>Working</Trans>;
    case "verifying":
      return <Trans>Verifying</Trans>;
    case "nudge_queued":
      return <Trans>Nudging</Trans>;
    case "completed":
      return <Trans>Done</Trans>;
    case "blocked":
      return <Trans>Needs you</Trans>;
    case "cancelled":
      return <Trans>Cancelled</Trans>;
  }
}

function goalStatusTone(status: ExpertGoalStatus): string {
  switch (status) {
    case "completed":
      return "bg-success/15 text-success";
    case "blocked":
      return "bg-warning/15 text-warning";
    case "cancelled":
      return "bg-foreground/10 text-muted-foreground";
    default:
      return "bg-foreground/10 text-foreground";
  }
}

/**
 * The /goal card in the unified Space chat. It stays one line — domain, outcome, the
 * proven Done-when count and live status — until opened, except that a goal waiting on a
 * decision opens itself so the question is the next thing the person sees.
 */
export function ExpertGoalCard({
  block,
  onResolveBlocker,
  expanded: expandedProp,
  onToggle,
}: {
  block: ExpertGoalBlock;
  /** Answers the blocker's question so the wrangler resumes the goal. */
  onResolveBlocker?: (goalId: string, resolution: string) => void | Promise<void>;
  /** Controlled disclosure, for tests and for a screen that keeps the card open. */
  expanded?: boolean;
  onToggle?: () => void;
}) {
  const { t } = useLingui();
  const question = expertGoalBlockerQuestion(block);
  const [open, setOpen] = useState(() => Boolean(question));
  const [resolution, setResolution] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const expanded = expandedProp ?? open;
  const progress = expertGoalCheckProgress(block.checks);
  const presentation = dantePresentationForGoal(block);
  const displayStatus = presentation.state === "respect-obstacle" ? "blocked" : block.status;
  const turns = Math.min(100, Math.round((block.turnsUsed / block.budgetTurns) * 100));
  const resolved = block.blocker?.resolvedAt ? block.blocker.resolution : undefined;
  // A goal that stops on a question discloses itself; the answer is the next thing to do.
  const blockedAt = question ? block.blocker?.createdAt : undefined;
  useEffect(() => {
    if (blockedAt) setOpen(true);
  }, [blockedAt]);

  async function submit() {
    const answer = resolution.trim();
    if (!answer || !onResolveBlocker || pending) return;
    setPending(true);
    setError(null);
    try {
      await onResolveBlocker(block.goalId, answer);
      setResolution("");
    } catch (err) {
      setError(errorText(err, t`Could not send this decision`));
    } finally {
      setPending(false);
    }
  }

  return (
    <Collapsible
      open={expanded}
      onOpenChange={(next) => {
        onToggle?.();
        if (expandedProp === undefined) setOpen(next);
      }}
      data-testid="expert-goal-card"
      className="w-[420px] max-w-full rounded-[18px] border border-border bg-muted"
    >
      <CollapsibleTrigger className="flex w-full min-w-0 cursor-pointer items-center gap-2.5 rounded-[18px] px-4 py-3 text-start outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-muted">
        <DanteCharacter
          state={presentation.state}
          taskId={block.goalId}
          taskName={block.outcome}
          size={32}
          motion="still"
          palette={presentation.state === "acknowledge-completion" ? "dispatch" : undefined}
        />
        <span data-testid="expert-goal-summary" className="min-w-0 flex-1 truncate text-[14px]">
          <span className="text-muted-foreground">{block.expertDomain}</span>
          <span aria-hidden className="mx-1.5 text-muted-foreground">
            ·
          </span>
          <span className="text-foreground" dir="auto">
            {block.outcome}
          </span>
        </span>
        <span
          data-testid="expert-goal-progress"
          className="shrink-0 text-[12.5px] tabular-nums text-muted-foreground"
        >
          {progress.verified}/{progress.total}
        </span>
        <span
          data-testid="expert-goal-status"
          className={`shrink-0 rounded-full px-2 py-0.5 text-[12px] ${goalStatusTone(presentation.state === "leave-question-open" ? "blocked" : displayStatus)}`}
        >
          {presentation.state === "leave-question-open" ? (
            <Trans>Unverified</Trans>
          ) : (
            goalStatusLabel(displayStatus)
          )}
        </span>
        <ChevronDown
          data-testid="expert-goal-chevron"
          aria-hidden
          size={14}
          strokeWidth={1.8}
          className={`shrink-0 text-muted-foreground transition-transform duration-150 motion-reduce:transition-none ${expanded ? "rotate-180" : ""}`}
        />
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div data-testid="expert-goal-details" className="border-t border-border px-4 pt-3 pb-3.5">
          <ol className="space-y-2">
            {block.checks.map((check) => {
              const evidence = expertGoalEvidenceLine(check);
              return (
                <li key={check.id} data-testid="expert-goal-check" className="flex gap-2">
                  <span
                    aria-hidden
                    className={`shrink-0 text-[13px] ${hasDanteVerificationEvidence(check) ? "text-success" : "text-muted-foreground"}`}
                  >
                    {hasDanteVerificationEvidence(check) ? "✓" : "○"}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-[13.5px] leading-[1.4] text-foreground" dir="auto">
                      {check.index}. {check.statement}
                    </span>
                    {evidence ? (
                      <span
                        data-testid="expert-goal-evidence"
                        className="mt-0.5 block truncate font-mono text-[12px] text-muted-foreground"
                        dir="auto"
                      >
                        {evidence}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ol>
          <div className="mt-3 flex items-center gap-2 text-[12px] text-muted-foreground">
            <span
              aria-hidden
              className="h-1 min-w-0 flex-1 overflow-hidden rounded-full bg-foreground/10"
            >
              <span
                className="block h-full rounded-full bg-foreground/40"
                style={{ width: `${turns}%` }}
              />
            </span>
            <span data-testid="expert-goal-turns" className="shrink-0 tabular-nums">
              {t`${block.turnsUsed}/${block.budgetTurns} turns`}
            </span>
          </div>
          {question ? (
            <div
              data-testid="expert-goal-blocker"
              className="mt-3 rounded-xl bg-warning/10 px-3 py-2.5"
            >
              <p className="text-[13px] leading-[1.45] text-foreground" dir="auto">
                {question}
              </p>
              {onResolveBlocker ? (
                <form
                  className="mt-2 flex items-center gap-2"
                  onSubmit={(event) => {
                    event.preventDefault();
                    void submit();
                  }}
                >
                  <Input
                    aria-label={t`Your decision`}
                    placeholder={t`Your decision`}
                    value={resolution}
                    disabled={pending}
                    onChange={(event) => setResolution(event.target.value)}
                    className="h-8 text-[13px]"
                  />
                  <Button
                    type="submit"
                    size="sm"
                    className="shrink-0 rounded-full"
                    disabled={!resolution.trim() || pending}
                  >
                    {pending ? t`Sending…` : t`Resolve`}
                  </Button>
                </form>
              ) : null}
              {error ? <p className="mt-1.5 text-[12px] text-destructive">{error}</p> : null}
            </div>
          ) : resolved ? (
            <p
              data-testid="expert-goal-resolution"
              className="mt-3 flex gap-1.5 text-[13px] leading-[1.45] text-muted-foreground"
              dir="auto"
            >
              <span aria-hidden className="text-success">
                ✓
              </span>
              {resolved}
            </p>
          ) : null}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
